import { useState, useRef, DragEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, File, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { colors, spacing } from '@/theme'
import { FileUtils } from '@/utils/fileUtils'

interface FileUploadProps {
  onFileSelect: (file: File) => void
  accept?: string
  maxSize?: number
  multiple?: boolean
  label?: string
  helpText?: string
  hideUploadedFiles?: boolean // Para ocultar la lista interna cuando se usa individualmente
  currentFile?: File | null // Archivo actual para mostrar preview
}

export function FileUpload({
  onFileSelect,
  accept = 'application/pdf,image/*',
  maxSize = 100 * 1024 * 1024, // 10MB
  multiple = false,
  label = 'Subir archivo',
  helpText,
  hideUploadedFiles = false,
  currentFile = null,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(
    currentFile ? [currentFile] : []
  )
  const [errors, setErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    processFiles(files)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      processFiles(files)
    }
  }

  const processFiles = (files: File[]) => {
    const newErrors: string[] = []
    const validFiles: File[] = []

    files.forEach((file) => {
      const error = FileUtils.validateFile(file, accept.split(','))
      if (error) {
        newErrors.push(`${file.name}: ${error}`)
      } else if (file.size > maxSize) {
        newErrors.push(`${file.name}: El archivo es demasiado grande. Máximo ${FileUtils.formatFileSize(maxSize)}`)
      } else {
        validFiles.push(file)
        onFileSelect(file)
      }
    })

    setErrors(newErrors)
    if (validFiles.length > 0) {
      setUploadedFiles((prev) => (multiple ? [...prev, ...validFiles] : validFiles))
    }
  }

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />

      {/* Zona de drop */}
      <motion.div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        animate={{
          borderColor: isDragging ? colors.primary[500] : colors.gray[300],
          backgroundColor: isDragging ? colors.primary[50] : colors.gray[50],
        }}
        style={{
          border: `3px dashed ${colors.gray[300]}`,
          borderRadius: '20px',
          padding: spacing[8],
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Efecto de ondas al arrastrar */}
        {isDragging && (
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
            }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.primary[200]} 0%, transparent 70%)`,
            }}
          />
        )}

        <div style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            animate={{
              y: isDragging ? [0, -10, 0] : 0,
            }}
            transition={{
              duration: 0.6,
              repeat: isDragging ? Infinity : 0,
            }}
          >
            <Upload
              size={48}
              color={isDragging ? colors.primary[500] : colors.gray[400]}
            />
          </motion.div>

          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: colors.gray[900],
              marginTop: spacing[3],
              marginBottom: spacing[1],
            }}
          >
            {isDragging ? 'Suelta el archivo aquí' : label}
          </motion.h3>

          <p
            style={{
              fontSize: '14px',
              color: colors.gray[600],
              marginBottom: spacing[2],
            }}
          >
            {helpText || 'Arrastra y suelta un archivo o haz clic para seleccionar'}
          </p>

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              padding: `${spacing[2]} ${spacing[4]}`,
              backgroundColor: colors.primary[500],
              color: colors.white,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              marginTop: spacing[2],
            }}
          >
            Seleccionar archivo
          </motion.div>

          <p
            style={{
              fontSize: '12px',
              color: colors.gray[500],
              marginTop: spacing[3],
            }}
          >
            Formatos: PDF, JPG, PNG • Máximo {FileUtils.formatFileSize(maxSize)}
          </p>
        </div>
      </motion.div>

      {/* Archivos subidos - Solo mostrar si no está oculto */}
      {!hideUploadedFiles && (
        <AnimatePresence>
          {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[3],
            }}
          >
            <h4
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: colors.gray[900],
              }}
            >
              Archivos subidos ({uploadedFiles.length})
            </h4>
            {uploadedFiles.map((file, index) => (
              <motion.div
                key={`${file.name}-${index}`}
                initial={{ opacity: 0, x: -20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                whileHover={{ x: 5, scale: 1.02 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[3],
                  padding: spacing[4],
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  // @ts-ignore
                  border: `2px solid ${colors.success[200]}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <motion.div
                  animate={{
                    rotate: [0, 10, -10, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: colors.success[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <File size={24} color={colors.success[600]} />
                </motion.div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing[2],
                      marginBottom: spacing[1],
                    }}
                  >
                    <p
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: colors.gray[900],
                      }}
                    >
                      {file.name}
                    </p>
                    <CheckCircle2 size={16} color={colors.success[600]} />
                  </div>
                  <p
                    style={{
                      fontSize: '12px',
                      color: colors.gray[600],
                    }}
                  >
                    {FileUtils.formatFileSize(file.size)} • Subido correctamente
                  </p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile(index)
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: colors.error[50],
                    color: colors.error[600],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={18} />
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      )}
      {/* Errores */}
      <AnimatePresence>
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              padding: spacing[4],
              backgroundColor: colors.error[50],
              borderRadius: '12px',
              border: `2px solid ${colors.error[600]}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[2],
                marginBottom: spacing[2],
              }}
            >
              <AlertCircle size={20} color={colors.error[600]} />
              <h5
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  // @ts-ignore
                  color: colors.error[700],
                }}
              >
                Error al subir archivo(s)
              </h5>
            </div>
            {errors.map((error, index) => (
              <p
                key={index}
                style={{
                  fontSize: '13px',
                  // @ts-ignore
                  color: colors.error[700],
                  marginTop: spacing[1],
                }}
              >
                • {error}
              </p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
